import { queryClient } from '@/infra/http'
import { userContentDetailsService } from '@/data/services/content/user-content-details-service'
import { userEnrollmentContentInfoService } from '@/data/services/content/user-enrollment-content-info-service'
import { productContentService } from '@/data/services/product/product-content-service'
import { PRODUCT_AVAILABLE_INFO_QUERY_KEY } from '@/data/services/product/product-content-service-types'
import { productFitScoreService } from '@/data/services/product/product-fit-score-service'
import {
  FormattedFitScore,
  PRODUCT_FIT_SCORE_QUERY_KEY
} from '@/data/services/product/product-fit-score-service-types'
import { requiresLearningPreferences } from '@/data/services/product/product-fit-score-service-utils'
import { checkFlagEnabled, FeatureFlagName } from '@/infra/feature-flag'

/**
 * Tranca única do contrato novo do fit-score (TS-9011).
 *
 * `ENABLE_USER_LEARNING_PREFERENCES` governa a feature "Objetivo e preferências de estudo"
 * inteira — página, rota, menu, modal e card do espaço do usuário. O fit-score depende dela de
 * verdade: a variação que lê preferências comenta dados que o usuário declara lá, e seus links
 * de edição apontam para a rota que a flag guarda. Com a feature off, exibi-la seria apontar um
 * problema e trancar a porta da solução.
 *
 * O gate mora **aqui** e não no componente porque o loader é a fonte única do fit para as duas
 * telas: a PDV e `/content/:id/fit-score`. Enquanto isso vivia num hook da PDV, a página lia
 * `useAsyncValue().fitScore` cru e renderizava a variação de idiomas com a feature desligada.
 *
 * Com a flag off o front se comporta como antes da TS-9011, **independente do que o backend
 * mande** — o que desacopla a ordem dos dois deploys:
 *
 * - variação que depende de preferências → não existe (`null`), o bloco não aparece e o CTA
 *   volta a ser matrícula direta;
 * - `selectedOffer` zerado → desliga de uma vez a pré-seleção do wizard, o refetch por oferta
 *   e o `?offerId=` na URL, que é o que `selectedOffer != null` habilita adiante;
 * - `offerId` da rota ignorado → a página pede o fit do produto, como hoje.
 *
 * Por ser o único ponto, nem `useContentFitScore` nem a raiz do wizard leem feature flag.
 */
const applyLearningPreferencesGate = (
  fitScore: FormattedFitScore | null,
  enabled: boolean
): FormattedFitScore | null => {
  if (enabled || !fitScore) return fitScore
  if (requiresLearningPreferences(fitScore.category)) return null
  return { ...fitScore, selectedOffer: null }
}

/**
 * Data loader da rota de detalhes do conteúdo (titulares e dependentes).
 *
 * Executa as queries em paralelo antes de renderizar a página:
 *
 * 1. `userContentDetails` — dados do produto (nome, formato, instituição, etc.)
 * 2. `userEnrollmentContentInfo` — elegibilidade e restrições de matrícula do usuário
 * 3. `productAvailableInfo` — cobertura de plano e planos disponíveis para o produto
 * 4. `fitScore` — compatibilidade do perfil com o curso (gated pela feature flag
 *    ENABLE_FIT_SCORE; falha silenciosa pra não derrubar a página inteira)
 *
 * Na PDV o fit-score é buscado sem `offerId`, então o backend devolve a oferta de maior
 * compatibilidade do produto. Em idiomas a PDV refaz a chamada por oferta conforme o
 * usuário seleciona no wizard (`useContentFitScore`) — a query key inclui o segmento de
 * oferta para que a chamada daqui seja aproveitada como a variante "sem oferta".
 *
 * A rota de recomendações (`/content/:id/fit-score?offerId=`) passa o `offerId` adiante: ali
 * o fit precisa descrever a MESMA oferta que a PDV exibiu, senão a página comenta um nível
 * diferente do que o usuário acabou de ver — e, quando a oferta do produto é compatível,
 * chega a devolvê-lo para a PDV num laço. Vindo da PDV a resposta já está em cache sob essa
 * key, então não há request novo; depois de um F5 é uma chamada só, com os dois ids.
 *
 * Os estados disponíveis (usado por `use-enrollment-wizard-flags` e `EnrollmentUnitSelect`)
 * são buscados diretamente por esses componentes ao montar. O wizard exibe um Skeleton
 * enquanto essa query carrega, evitando blink de conteúdo.
 *
 * `isDependentContext` vem explicitamente do wiring da rota — não pode ser inferido
 * via `window.location` aqui porque loaders rodam ANTES da navegação ser commitada
 * (em navegação SPA o pathname ainda é o da página anterior).
 */
export async function loadContentDetails(
  contentId: number,
  isDependentContext = false,
  offerId: number | null = null
) {
  // Ver `applyLearningPreferencesGate`: com a feature off não existe escopo por oferta, então o
  // `offerId` da rota não é honrado e o fit é o do produto — o mesmo request de hoje.
  const learningPreferencesEnabled = checkFlagEnabled(
    FeatureFlagName.ENABLE_USER_LEARNING_PREFERENCES
  )
  const scopedOfferId = learningPreferencesEnabled ? offerId : null

  const [userContentDetails, userEnrollmentContentInfo, productAvailableInfo, rawFitScore] =
    await Promise.all([
      queryClient.fetchQuery({
        queryKey: ['user-content-details', contentId],
        queryFn: () => userContentDetailsService.fetchUserContentDetails(contentId),
        staleTime: 1000
      }),
      queryClient.fetchQuery({
        queryKey: ['user-enrollment-content-info', contentId],
        queryFn: () => userEnrollmentContentInfoService.fetchUserEnrollmentContentInfo(contentId),
        staleTime: 1000
      }),
      queryClient.fetchQuery({
        queryKey: [PRODUCT_AVAILABLE_INFO_QUERY_KEY, contentId],
        queryFn: () =>
          productContentService.fetchAvailableInfo({
            productId: contentId,
            isDependentContext
          }),
        staleTime: 0
      }),
      // Fit-score gated pela feature flag, com fallback silencioso pra não derrubar
      // a página inteira se a API falhar (feature é secundária ao core da PDV).
      checkFlagEnabled(FeatureFlagName.ENABLE_FIT_SCORE)
        ? queryClient
            .fetchQuery({
              queryKey: [PRODUCT_FIT_SCORE_QUERY_KEY, contentId, scopedOfferId],
              queryFn: () => productFitScoreService.fetchProductFitScore(contentId, scopedOfferId),
              staleTime: 1000
            })
            .catch(() => null)
        : null
    ])

  const fitScore = applyLearningPreferencesGate(rawFitScore, learningPreferencesEnabled)

  // A resposta se identifica: `selectedOffer` diz de qual oferta é o cálculo. Cacheá-la
  // também sob a key daquela oferta poupa um round-trip em toda PDV de idioma — sem isso
  // o `useContentFitScore` pediria de novo exatamente esta resposta assim que o wizard
  // resolvesse a oferta pré-selecionada, que é justamente a que veio aqui.
  if (fitScore?.selectedOffer) {
    queryClient.setQueryData(
      [PRODUCT_FIT_SCORE_QUERY_KEY, contentId, fitScore.selectedOffer.offerId],
      fitScore
    )
  }

  return {
    userContentDetails,
    userEnrollmentContentInfo,
    productAvailableInfo,
    fitScore
  }
}
